import { useParams } from 'react-router-dom'
import { formatEur, taxLabel } from '@wf/shared'
import { ApiError } from '../api/client'
import { useDecideEstimate, usePublicEstimate } from './usePublicEstimate'

/**
 * Öffentliche KV-Freigabe (ohne Login, Route /freigabe/:token). Der Kunde sieht den reduzierten
 * Kostenvoranschlag (keine Secrets — die API liefert bewusst nur PublicEstimateResponse) und entscheidet
 * per Knopf. Geld ausschließlich über @wf/shared (formatEur); Netto/USt/Brutto kommen fertig gerechnet
 * (Kleinunternehmer-aware) aus der Antwort. Eigenständiges Layout ohne App-Shell/Nav.
 */
export function PublicEstimatePage() {
  const { token = '' } = useParams()
  const { data, isLoading, error } = usePublicEstimate(token)
  const decide = useDecideEstimate(token)

  if (isLoading) {
    return (
      <main className="wf-public">
        <div className="wf-public__card">
          <p className="muted">Kostenvoranschlag wird geladen…</p>
        </div>
      </main>
    )
  }
  if (error || !data) {
    const gone = error instanceof ApiError && error.status === 404
    return (
      <main className="wf-public">
        <div className="wf-public__card">
          <h1 className="wf-public__title">Kostenvoranschlag</h1>
          <p className="error" role="alert">
            {gone ? 'Dieser Link ist ungültig oder abgelaufen.' : 'Der Kostenvoranschlag konnte nicht geladen werden.'}
          </p>
        </div>
      </main>
    )
  }

  const { order, tasks, parts, items, anbieter } = data

  return (
    <main className="wf-public">
      <div className="wf-public__card">
        <div className="wf-public__head">
          <h1 className="wf-public__title">Kostenvoranschlag</h1>
          <p className="sub">{anbieter.name}</p>
        </div>

        <div className="wf-public__meta">
          <span className="wf-plate">
            <span className="wf-plate__eu">D</span>
            <span className="wf-plate__nr">{order.plate ?? '—'}</span>
          </span>
          <span>
            {order.vehicleModel ?? 'Fahrzeug'}
            {order.nr ? ` · ${order.nr}` : ''}
          </span>
        </div>

        {tasks.length > 0 ? (
          <section className="wf-public__section">
            <h2 className="wf-public__h2">Arbeiten</h2>
            <ul className="wf-public__tasks">
              {tasks.map((task, index) => (
                <li key={index}>{task}</li>
              ))}
            </ul>
          </section>
        ) : null}

        {parts.length > 0 || items.length > 0 ? (
          <section className="wf-public__section">
            <h2 className="wf-public__h2">Positionen</h2>
            <div className="wf-public__lines">
              {parts.map((part, index) => (
                <div key={`p${index}`} className="wf-public__line">
                  <span>
                    {part.qty}× {part.name}
                    {part.partNumber ? ` (${part.partNumber})` : ''} · {taxLabel(part.vatRate)}
                  </span>
                  <span className="wf-tnum">{formatEur(Math.round(part.priceCents * part.qty))}</span>
                </div>
              ))}
              {items.map((item, index) => (
                <div key={`i${index}`} className="wf-public__line">
                  <span>
                    {item.qty}× {item.name} · {taxLabel(item.vatRate)}
                  </span>
                  <span className="wf-tnum">{formatEur(Math.round(item.priceCents * item.qty))}</span>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <div className="wf-public__totals">
          <div className="wf-public__line">
            <span>Gesamt netto</span>
            <span className="wf-tnum">{formatEur(order.netCents)}</span>
          </div>
          {order.byRate.map((group) => (
            <div key={group.rate} className="wf-public__line">
              <span>
                {group.rate} % USt auf {formatEur(group.netCents)}
              </span>
              <span className="wf-tnum">{formatEur(group.vatCents)}</span>
            </div>
          ))}
          <div className="wf-public__line wf-public__line--gross">
            <span>Bruttogesamtbetrag</span>
            <span className="wf-tnum">{formatEur(order.grossCents)}</span>
          </div>
          {order.kleinunternehmer ? (
            <p className="wf-public__hint">Kleinunternehmer gemäß § 19 UStG – keine Umsatzsteuer ausgewiesen.</p>
          ) : null}
        </div>

        {order.estimateStatus === 'approved' ? (
          <p className="wf-public__decision wf-public__decision--ok" role="status">
            Sie haben diesen Kostenvoranschlag freigegeben. Danke!
          </p>
        ) : order.estimateStatus === 'declined' ? (
          <p className="wf-public__decision wf-public__decision--no" role="status">
            Sie haben diesen Kostenvoranschlag abgelehnt.
          </p>
        ) : (
          <div className="wf-public__actions">
            <button type="button" className="wf-btn wf-btn--ghost" disabled={decide.isPending} onClick={() => decide.mutate('decline')}>
              Ablehnen
            </button>
            <button type="button" className="wf-btn wf-btn--primary" disabled={decide.isPending} onClick={() => decide.mutate('approve')}>
              Freigeben
            </button>
          </div>
        )}
        {decide.error instanceof ApiError ? (
          <p className="error" role="alert">
            {decide.error.message}
          </p>
        ) : null}
      </div>
    </main>
  )
}
